// Drag-by-header for the TSIA2 pop-outs (the calculator panel, the explanation
// popup).
//
// The house pattern is a local `dragMove` in each player rather than a shared
// module -- SATModule and DigitalSAT/Module each carry their own copy. This is
// TSIA2's, written once here so the calculator and the review popup share it
// instead of the feature growing a third and fourth copy.
//
// Anything whose header is styled `cursor: grab` should be wired to this: a
// grab cursor on something that cannot be grabbed is worse than no cursor.
import React, { useEffect, useRef } from 'react';

export function useDraggable<T extends HTMLElement>() {
    const ref = useRef<T>(null);
    const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

    useEffect(() => {
        const onMove = (event: MouseEvent) => {
            if (!drag.current || !ref.current) return;
            // Keep at least a sliver on screen; a panel dragged fully off has
            // no header left to drag it back by.
            const left = drag.current.left + event.clientX - drag.current.x;
            const top = drag.current.top + event.clientY - drag.current.y;
            const maxLeft = window.innerWidth - 60;
            const maxTop = window.innerHeight - 40;
            ref.current.style.left = `${Math.min(Math.max(left, -ref.current.offsetWidth + 60), maxLeft)}px`;
            ref.current.style.top = `${Math.min(Math.max(top, 0), maxTop)}px`;
        };
        const onUp = () => { drag.current = null; };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, []);

    const onHeaderMouseDown = (event: React.MouseEvent) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        // Switch to pixel positioning on first grab, so a panel placed by a
        // CSS `right`/`calc()` rule does not jump when it starts moving.
        ref.current.style.left = `${rect.left}px`;
        ref.current.style.top = `${rect.top}px`;
        ref.current.style.right = 'auto';
        ref.current.style.bottom = 'auto';
        ref.current.style.margin = '0';
        drag.current = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
    };

    return { ref, onHeaderMouseDown };
}
